/**
 * Created by Todd Yu on 06/20/2020.
 * Description:
 * Main Grid Page for Administrative Console
 * Handles all logic for creating, reading, updating, deleting notes/entities
 * ------ maintenance history ------
 * 1. modified by Teddy Ding on 12/15/20
 * support tamale slide-sheet component
 * fix the problem of displaying and changing the data of notetype and entitytype
 * refine the code to match coding standard
 *
 * 2. modified by Lucas Wang on 08/19/21
 * Fixes some style and logic problems of EntryTypes and EntityTypes pages
 * Adds Users and System Credentials pages to Admin Console
 */

import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ColDef, GridOptions, GridApi } from 'ag-grid-community';

import { Subject, Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { AaclKeywordSearchOptions } from 'aacl-keyword-search';
import { AdminPageSetting, AdminPageFeedbackTypes, AdminPageFeedback } from '../../admin-page-setting.model';
import { adminPageRoutingMap, adminPagesConstants } from '../../admin-page-setting.constants';
import { NavUsers, NavEntityTypes, NavNoteTypes, DeleteUsers, DeleteNoteType, EditDefaultNoteType, ToggleNoteDisplay, DeleteEntityType, ToggleEntityDisplay, EditDefaultEntityType, ClearStatus } from '../../../../redux/actions/admin-page.actions';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { SystemUser } from '../../../../tamalelibs/models/user.model';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { AppState } from '../../../../redux';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { AdminPageState, AdminPageStatusTypes, AdminPageTypes } from '../../../../redux/reducers/admin-page.reducer';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { SplitButtonConfig, ButtonConfig } from '../../../../widgets/split-button/split-button.model';
import { AdminPagesService } from '../../../../tamalelibs/services/admin-page.service';
import { FileHelperService } from '../../../../services/file-helper.service';
import { businessConstants } from '../../../../constants/business.constants';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'admin-page-grid',
    templateUrl: './admin-grid-page.component.html',
    styleUrls: ['./admin-grid-page.component.scss']
})
export class AdminGridPageComponent implements OnInit, AfterViewInit, OnDestroy {
    currPage: AdminPageSetting;
    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    gridKeywordSearchConfig: AaclKeywordSearchOptions = new AaclKeywordSearchOptions('', 250, 'Quick Search');
    isSplitButton = false;
    rowData: Array<any> = [];
    splitButtonConfig: SplitButtonConfig;
    loading = true;

    private _currPageUrl: string;
    private _currUser: SystemUser;
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;

    constructor(
        private _adminPagesService: AdminPagesService,
        private _alert: AlertWindowService,
        private _fileHelperService: FileHelperService,
        private _route: ActivatedRoute,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _toast: ToastService,
    ) {
    }

    /** Initialize all subscriptions, services, and components */
    ngOnInit() {
        const _pageType = this._route.snapshot.data['page'];
        this._retrieveCurrentUser();
        this._handlePageRouting(_pageType);
        this._initSubscriptions();
        this._initGrid(_pageType);
    }

    /** Initialize editor in right hand side of page for note and entity types */
    ngAfterViewInit(): void {
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    /** Subscribe to store changes related to current user */
    private _retrieveCurrentUser() {
        this._destroySubscriptions.push(
            this._store.pipe(
                select('user')
            ).subscribe(user => this._currUser = user)
        );
    }

    /** Subscribe to component feedback, keyword search, and store changes related to current admin page */
    private _initSubscriptions() {
        this._destroySubscriptions.push(this._store.pipe(
            select('adminPage')
        ).subscribe((pageState: AdminPageState) => this._parsePageState(pageState))
        );
        this._destroySubscriptions.push(
            this.currPage.feedbackSubject$.subscribe(feedback => this._handleGridFeedback(feedback))
        );
        this._destroySubscriptions.push(this.gridKeywordSearchConfig.feedbackSubject$.subscribe(
            event => {
                this._gridApi.setQuickFilter(event.payload.data);
                // when filter has no data to show,then overlayNoRowsTemplate works
                if (this._gridApi.getDisplayedRowCount() === 0) {
                    this._gridApi.showNoRowsOverlay();
                } else {
                    this._gridApi.hideOverlay();
                }
            })
        );
    }

    /** Initialize default column settings for grid */
    private _initGrid(_pageType: string) {
        this.columnDefs = this._adminPagesService.getGridColumnDefsByType(_pageType);
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true,
                filter: false,
                minWidth: 170,
                cellStyle: {
                    'overflow': 'hidden',
                    'text-overflow': 'ellipsis',
                    'display': 'block',
                    'white-space': 'nowrap',
                }
            },
            cacheBlockSize: 50,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            sortingOrder: ['asc', 'desc'],
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
            overlayNoRowsTemplate: '<span style=\'font-size: 20px;background-color:white;color:rgba(0,0,0,0.45)\'>No Data Found</span>'
        };
    }

    /** Initialize default settings for split button when current page is split button */
    private _initSplitButton() {
        this.splitButtonConfig = {
            primaryAction: {
                displayText: this.currPage.additionalActions.create.buttonName,
                onClick: () => {
                    this.onButtonClick();
                }
            } as ButtonConfig,
            secondaryActions: [
                {
                    displayText: 'Download CSV template',
                    onClick: () => {
                        this._fileHelperService.downloadCsvFile();
                    }
                },
                // Hide this code for 22.1, TAM-37050
                // {
                //     displayText: 'Import CSV',
                //     onClick: () => {
                //         console.log('Import CSV');
                //     }
                // },
                // {
                //     displayText: 'Export as CSV',
                //     onClick: () => {
                //         console.log('Export as CSV');
                //     }
                // }
            ],
            feedbackSubject$: new Subject<any>()
        } as SplitButtonConfig;
    }

    /** General handler for feedback from grid components */
    private _handleGridFeedback(feedback: AdminPageFeedback) {
        switch (feedback.type) {
            case AdminPageFeedbackTypes.EDIT:
                this._handleEdit(feedback);
                break;
            case AdminPageFeedbackTypes.DELETE:
                this._handleDelete(feedback);
                break;
            case AdminPageFeedbackTypes.SWITCH_DEFAULT:
                this._handleSwitchDefault(feedback);
                break;
            case AdminPageFeedbackTypes.TOGGLE_WEB:
                this._handleToggleCredential(feedback);
                break;
            default:
                break;
        }
    }

    /** Delete a note or entity type after verification and confirmation from the user.
    * Dispatches a delete message to the store and updates page state. */
    private _handleDelete(feedback: AdminPageFeedback) {
        const deletedItemId = feedback.payload.data.id;
        if (this._verifyDefault(deletedItemId)) {
            this._alert.error(`Delete ${this.currPage.id}`, [`Cannot delete ${feedback.payload.data['name']} because it is currently a default type`]);
            return;
        }
        /**Distinguish between USERS and NOTE_TYPES,ENTITY_TYPES*/
        const name = this._currPageUrl === AdminPageTypes.USERS ? feedback.payload.data.username : feedback.payload.data.name;

        const deleteAction = {
            id: deletedItemId,
            data: feedback.payload.data,
            successMessage: `${name} successfully deleted.`,
        };
        this._alert.warn(`Delete ${this.currPage.id}`, [`Deleting ${name} will remove ${name}'s ability to connect to the server. All of the ${name}'s notes, files, associated relationships and other previously submitted content will remain intact`], 'Delete', 'Cancel').pipe(
            take(1)
        ).subscribe(confirmation => {
            if (confirmation) {
                switch (this._currPageUrl) {
                    case AdminPageTypes.USERS:
                        this._store.dispatch(new DeleteUsers(deleteAction));
                        break;
                    case AdminPageTypes.NOTE_TYPES:
                        this._store.dispatch(new DeleteNoteType(deleteAction));
                        break;
                    case AdminPageTypes.ENTITY_TYPES:
                        this._store.dispatch(new DeleteEntityType(deleteAction));
                        break;
                    default:
                        break;
                }
            }
        });
    }

    /** Helper function to verify the current default type */
    private _verifyDefault(deletedId: string): boolean {
        switch (this._currPageUrl) {
            case AdminPageTypes.USERS:
                return this._verifyNoteTypes(deletedId);
            case AdminPageTypes.NOTE_TYPES:
                return this._verifyNoteTypes(deletedId);
            case AdminPageTypes.ENTITY_TYPES:
                return this._verifyEntityTypes(deletedId);
            default:
                break;
        }
    }

    /** Helper function that determines if the selected note type is a default type */
    private _verifyNoteTypes(deletedId: string): boolean {
        for (let i = 0; i < this.rowData.length; i += 1) {
            if (this.rowData[i]['id'] === deletedId
                && ((this.rowData[i]['default-note'] && this.rowData[i]['default-note'] === true)
                    || (this.rowData[i]['default-event'] && this.rowData[i]['default-event'] === true))) {
                return true;
            }
        }
        return false;
    }

    /** Helper function that determines if the selected entity type is a default type */
    private _verifyEntityTypes(deletedId: string): boolean {
        for (let i = 0; i < this.rowData.length; i += 1) {
            if (this.rowData[i]['id'] === deletedId && this.rowData[i]['default-entity-type']
                && this.rowData[i]['default-entity-type'] === true) {
                return true;
            }
        }
        return false;
    }

    /** Triggered when user edits. Opens editor menu and passes current note/entity data to editor */
    private _handleEdit(feedback: AdminPageFeedback) {
        if (this.currPage.id === adminPagesConstants.users.id) {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.ADMIN_CONSOLE_USERS_EDITOR,
                payload: feedback.payload.data
            });
        } else if (this.currPage.id === adminPagesConstants.noteTypes.id) {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.ADMIN_CONSOLE_NOTE_EDITOR,
                payload: feedback.payload.data
            });
        } else if (this.currPage.id === adminPagesConstants.entityTypes.id) {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.ADMIN_CONSOLE_ENTITY_EDITOR,
                payload: feedback.payload.data
            });
        }
    }

    /** Helper function for toggling 'display in TamaleWeb'. Dispatches an edit message to the store and updates the page state. */
    private _handleToggleCredential(feedback: AdminPageFeedback) {
        const togglePayload = {
            name: feedback.payload.data['name'],
            id: feedback.payload.data['id'],
            data: feedback.payload.data,
            credentialField: feedback.payload.credentialField,
            successMessage: ''
        };
        switch (this._currPageUrl) {
            case AdminPageTypes.NOTE_TYPES:
                togglePayload.successMessage = `${togglePayload.name} is now ${togglePayload.data['display-web'] ? '' : 'not'} displayed in Tamale Web`;
                this._store.dispatch(new ToggleNoteDisplay(togglePayload));
                break;
            case AdminPageTypes.ENTITY_TYPES:
                togglePayload.successMessage = `${togglePayload.name} is now ${togglePayload.data['display-web'] ? '' : 'not'} displayed in Tamale Web`;
                this._store.dispatch(new ToggleEntityDisplay(togglePayload));
                break;
            case AdminPageTypes.USERS:
                togglePayload.successMessage = `${togglePayload.name} is now ${togglePayload.data['display-web'] ? '' : 'not'} displayed in Tamale Web`;
                this._store.dispatch(new ToggleEntityDisplay(togglePayload));
                break;
            default:
                break;
        }
    }

    /** Helper function for switching the default type. Dispatches edit default action to the store and updates the page state. */
    private _handleSwitchDefault(feedback: AdminPageFeedback) {
        const togglePayload = {
            name: feedback.payload.data['name'],
            id: feedback.payload.data['id'],
            data: feedback.payload.data,
            defaultTypeField: feedback.payload.defaultTypeField,
            successMessage: ''
        };
        switch (this._currPageUrl) {
            case AdminPageTypes.NOTE_TYPES:
                if (feedback.payload.defaultTypeField === 'default-event') {
                    togglePayload.successMessage = `Default Event Type changed to ${togglePayload.name}`;
                } else if (feedback.payload.defaultTypeField === 'default-note') {
                    togglePayload.successMessage = `Default Entry Type changed to ${togglePayload.name}`;
                }
                this._store.dispatch(new EditDefaultNoteType(togglePayload));
                break;
            case AdminPageTypes.ENTITY_TYPES:
                togglePayload.successMessage = `Default Entity Type changed to ${togglePayload.name}`;
                this._store.dispatch(new EditDefaultEntityType(togglePayload));
                break;
            case AdminPageTypes.USERS:
                break;
            default:
                break;
        }
    }

    /** Retrieves the current page from the route and dispatches a navigation action to the store to retrieve note/entity types data. */
    private _handlePageRouting(currPageUrl: string) {
        this._currPageUrl = currPageUrl;
        this.currPage = adminPageRoutingMap.get(this._currPageUrl);
        this.isSplitButton = this._currPageUrl === AdminPageTypes.USERS;
        switch (this._currPageUrl) {
            case AdminPageTypes.USERS:
                this._store.dispatch(new NavUsers());
                this._initSplitButton();
                break;
            case AdminPageTypes.NOTE_TYPES:
                this._store.dispatch(new NavNoteTypes());
                break;
            case AdminPageTypes.ENTITY_TYPES:
                this._store.dispatch(new NavEntityTypes());
                break;
            default:
                break;
        }
    }

    /** Triggered when create button is clicked; opens editor menu */
    onButtonClick() {
        if (this.currPage.id === adminPagesConstants.users.id) {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.ADMIN_CONSOLE_USERS_EDITOR
            });
        } else if (this.currPage.id === adminPagesConstants.noteTypes.id) {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.ADMIN_CONSOLE_NOTE_EDITOR
            });
        } else if (this.currPage.id === adminPagesConstants.entityTypes.id) {
            this._slideSheetService.slideSheetActionSubject$.next({
                type: SlideSheetActionTypes.ADMIN_CONSOLE_ENTITY_EDITOR
            });
        }
    }

    /** Notify user of failure/success, then clear the status message. Handle changes in the page state */
    private _parsePageState(state: AdminPageState) {
        if (state.successStatus === AdminPageStatusTypes.FAILURE) {
            // should not notify user with toast notification when the error is ERR_DUPLICATE
            if (state.failureMessage.search('ERR_DUPLICATE') === -1) {
                this._toast.notify({
                    style: NotificationStyles.Error,
                    message: state.failureMessage
                });
            }
            this._store.dispatch(new ClearStatus());
            return;
        }
        if (state.successStatus === AdminPageStatusTypes.SUCCESS && state.successMessage !== '') {
            this._toast.notify({
                style: NotificationStyles.Success,
                message: state.successMessage
            });
            this._store.dispatch(new ClearStatus());
            return;
        }
        switch (state.currentPage) {
            case AdminPageTypes.USERS:
                this.rowData = Object.assign([], state.currentPageData['user-list']);
                break;
            case AdminPageTypes.NOTE_TYPES:
                this.rowData = Object.assign([], state.currentPageData['entry-type-list']);
                break;
            case AdminPageTypes.ENTITY_TYPES:
                this.rowData = Object.assign([], state.currentPageData['entity-type-list']);
                break;
            case AdminPageTypes.SYSTEM_CREDENTIALS:
                const user: any = Object.assign({}, this._currUser.credential);
                user.name = this._currUser.loginName + ' ' + businessConstants.common.currentUser;
                this.rowData = [user];
                break;
            default:
                break;
        }
        // set the loading bar as false, since the datasource of grid has been assigned a value
        this.loading = false;
    }

    /** Initialize grid API and resize columns */
    onGridReady(params) {
        this._gridApi = params.api;
        switch (this._currPageUrl) {
            case 'users':
                this._gridApi.sizeColumnsToFit();
                break;
            case 'entry-types':
                this._gridApi.sizeColumnsToFit();
                break;
            case 'entity-types':
                this._gridApi.sizeColumnsToFit();
                break;
            case 'system-credentials':
                this._gridApi.sizeColumnsToFit();
                break;
            default:
                break;
        }
    }
}
